import * as THREE from 'three';

import {
    resolveEntityItemAnchorWorldPosition,
    resolveEntityItemWorldTransform,
    resolveLayoutAnchorWorldPosition,
} from '@toolproof-core/visualization/_pure/entity-anchor-resolution';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { parseWorkflowStateEntryConstellationKey } from '@/workflow/_pure/keyCodec';

export function derivePanelCameraGeometry(
    frame: SpaceDerivationFrame, stepKey: string, radius?: number, resourceType?: string,
) {
    const entityConstellations = frame.constellations.filter((entry) => entry.constellationKind === 'entity');
    const context = {
        entityConstellations,
        entityItemsByCollectionKey: new Map(frame.itemCollections.flatMap((collection) =>
            collection.itemCollectionKind === 'entity' ? [[collection.itemCollectionKey, collection.items] as const] : [],
        )),
        itemParametricStateByKey: frame.itemParametricState?.entityItemByKey ?? {},
        entityConstellationParametricStateByKey: frame.constellationParametricState?.entityConstellationByKey ?? {},
    };
    const points: [number, number, number][] = [];
    for (const constellation of entityConstellations) {
        const parsed = parseWorkflowStateEntryConstellationKey(constellation.constellationKey);
        if (parsed?.stepKey !== stepKey || resourceType !== undefined && parsed.resourceTypeHandle !== resourceType) continue;
        const layout = context.entityConstellationParametricStateByKey[constellation.constellationKey]?.layout;
        if (layout?.layoutKind !== 'spoke') continue;
        const items = context.entityItemsByCollectionKey.get(constellation.itemCollectionKey);
        const itemCenters = items?.flatMap((item) => {
            const center = resolveEntityItemAnchorWorldPosition({ itemKey: item.itemKey, anchorKey: 'center', context });
            return center ? [center.position] : [];
        }) ?? [];
        if (itemCenters.length > 0) points.push(...itemCenters);
        else {
            const anchor = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
            if (anchor) points.push(anchor);
        }
        for (const item of items ?? []) {
            const shape = context.itemParametricStateByKey[item.itemKey]?.shape;
            const transform = resolveEntityItemWorldTransform({ itemKey: item.itemKey, context });
            if (shape?.shapeKind !== 'panel' || !transform) continue;
            for (const x of [-shape.width / 2, shape.width / 2]) for (const y of [-shape.height / 2, shape.height / 2]) {
                const corner = new THREE.Vector3(x, y, 0).applyQuaternion(transform.rotation).add(transform.position);
                points.push([corner.x, corner.y, corner.z]);
            }
        }
    }
    if (points.length === 0) return undefined;
    const natural = resolveLayoutAnchorWorldPosition({
        anchor: { anchorKind: 'item', anchorItemKey: 'item:entity:TYPE-Natural' }, context,
    });
    if (radius !== undefined) {
        for (const type of ['TYPE-Natural', 'TYPE-Boolean']) {
            const position = resolveLayoutAnchorWorldPosition({
                anchor: { anchorKind: 'item', anchorItemKey: `item:entity:${type}` }, context,
            });
            if (!position) continue;
            const angle = Math.atan2(position[2], position[0]);
            points.push([radius * Math.cos(angle), position[1], radius * Math.sin(angle)]);
        }
    }
    const min = [0, 1, 2].map((axis) => Math.min(...points.map((point) => point[axis])));
    const max = [0, 1, 2].map((axis) => Math.max(...points.map((point) => point[axis])));
    const center = min.map((value, axis) => (value + max[axis]) / 2) as [number, number, number];
    return { center, points, naturalAngle: natural ? Math.atan2(natural[2], natural[0]) : undefined };
}

export function toCameraViewId(stepKey: string, resourceType: string, kind: 'folded' | 'operation' | 'glide'): string {
    return `workflow:${encodeURIComponent(stepKey)}:${encodeURIComponent(resourceType)}:${kind}`;
}
